import React from "react";
import {Box, styled, Typography} from "@mui/material";
import BackGo from "@/components/back-go/BackGo";
import {useTranslation} from "react-i18next";

const BoldTypography = ({children}: { children: string }) => {
  return (
    <Typography variant="body1" sx={{color: "gray", fontWeight: "bold"}} gutterBottom>{children}</Typography>
  )
}

const ContentTypography = ({children}: { children: string }) => {
  return (
    <Typography sx={{color: "gray"}} gutterBottom>{children}</Typography>
  )
}

const H6 = ({children}: { children: string }) => {
  return (
    <Typography variant="h6" sx={{color: "gray"}} gutterBottom>{children}</Typography>
  )
}

export interface UserAgreementProps {
  type?: any
}


function NGUserAgreement(props: UserAgreementProps) {
  const {t} = useTranslation();

  return (
    <Box sx={{width: 'calc(100% - 16px)', marginBottom: '24px'}}>
      {props.type !== 'login' && <BackGo title={t("common.User Agreement")}></BackGo>}
      <Box sx={{padding: "24px", backgroundColor: '#ffffff', marginTop: '-10px'}}>
        <H6>NGTECO Cloud Time & Attendance System</H6>
        <H6>End User License Agreement ("EULA")</H6>
        <H6>Last Updated: [February 10, 2026]</H6>
        <br/>
        <ContentTypography>IMPORTANT: READ THIS END USER LICENSE AGREEMENT ("EULA") CAREFULLY BEFORE CONTINUING
          REGISTRATION. BY CLICKING THE “I ACCEPT” BUTTON, YOU SIGNIFY THAT YOU HAVE READ, UNDERSTOOD AND AGREE TO
          FOLLOW THE TERMS AND CONDITIONS OF THIS EULA, INCLUDING, WITHOUT LIMITATION, THE TERMS AND CONDITIONS OF
          DOCUMENTS WHICH ARE INCORPORATED BY REFERENCE HEREIN. IF YOU DO NOT AGREE TO ALL THE TERMS AND CONDITIONS IN
          THIS EULA, YOU MUST SELECT THE “I DECLINE” BUTTON AND MAY NOT USE THE NGTECO PLATFORM.</ContentTypography>
        <H6>A. Definitions</H6>
        <ContentTypography>“Order” refers to your agreement to license the NGTECO Time & Attendance System through an
          NGTECO authorized sales channel.</ContentTypography>
        <br></br>
        <ContentTypography>“NGTECO” refers to NGTECO and its affiliated entities.</ContentTypography>
        <br></br>
        <ContentTypography>“Platform” refers to the NGTECO cloud-based time & attendance software platform and any
          associated web portals, mobile applications, devices or related services (collectively, the
          “Services”).</ContentTypography>
        <br></br>
        <ContentTypography>“You” or “Your” refer to any individual or entity ordering or using NGTECO Devices or the
          Platform.</ContentTypography>
        <br></br>
        <ContentTypography>“Customer” has the same meaning as “You” when You are acting as an employer or business using
          the Platform for workforce management.</ContentTypography>
        <br></br>
        <ContentTypography>“Your Data” refers to any data that you voluntarily submit to the Platform in connection with
          account registration and subsequent lawful use of the Platform, including account credentials, configuration
          information, and, where applicable, operational data generated by the Services (such as time records,
          schedules and related information). For clarity, this EULA governs only data processed after you activate and
          use the Platform following account registration.</ContentTypography>
        <br></br>
        <ContentTypography>“Payment Method” refers to a current, valid, accepted method of payment, which may include
          payment through a third party.</ContentTypography>
        <br></br>
        <ContentTypography>As used in this EULA, “Biometric Identifier”, “Biometric Information”, and “Biometric Data”
          shall have the meanings defined under applicable biometric privacy laws, including but not limited to the
          Illinois Biometric Information Privacy Act (740 ILCS 14/1 et seq.). Biometric Identifiers may include, as
          applicable, fingerprints and scans of face geometry.</ContentTypography>
        <br></br>
        <ContentTypography>“Employee” means any worker, contractor or individual enrolled by the Customer on a Device or
          via the Platform for identification or time & attendance purposes.</ContentTypography>
        <br/>
        <H6>B. Rights Granted</H6>
        <ContentTypography>Upon NGTECO’s acceptance of your Order and until your Order is canceled or otherwise
          terminated, you have the nonexclusive, non-assignable limited right to access and use the Platform and
          Services for your internal business operations only, subject to the terms of this EULA and the applicable
          Order and Subscription Agreement.</ContentTypography>
        <br/>
        <ContentTypography>You acknowledge that NGTECO has no obligation to deliver any physical copies of the Platform
          software and that the Services are provided electronically.</ContentTypography>
        <br/>
        <H6>C. Ownership and Restrictions</H6>
        <ContentTypography>You retain all ownership and intellectual property rights in and to Your
          Data.</ContentTypography>
        <br/>
        <ContentTypography>NGTECO retains all ownership and intellectual property rights to the Platform, Devices, and
          all associated software, technology, documentation, and services. You may not remove or modify any program
          markings or proprietary rights notices.</ContentTypography>
        <br/>
        <ContentTypography>You may not, and may not cause or permit others to: (a) copy, reproduce, modify or create
          derivative works of the Platform; (b) disassemble, decompile, reverse engineer or otherwise attempt to derive
          source code from any software in the Platform, except to the extent such restrictions are expressly prohibited
          by applicable law; (c) access or use the Platform to build or support, and/or assist a third party in building
          or supporting, products or services competitive to NGTECO; (d) make the Platform or any related materials
          available to any third party for that third party’s own business operations, time-sharing, service bureau or
          similar use; or (e) sell, rent, lease, sublicense, assign, distribute, display, host, outsource or otherwise
          commercially exploit the Platform, except as expressly permitted in this EULA.</ContentTypography>
        <br/>
        <ContentTypography>You agree to make every reasonable effort to prevent unauthorized third parties from
          accessing the Platform.</ContentTypography>
        <br/>
        <H6>D. Updates</H6>
        <ContentTypography>NGTECO may update, patch, modify or enhance the Platform from time to time. You agree to
          follow any reasonable instructions provided by NGTECO in relation to such updates. Certain updates may be
          applied automatically without additional notice in order to maintain security, stability or legal
          compliance.</ContentTypography>
        <br/>
        <H6>E. Your Data</H6>
        <ContentTypography>You are solely responsible for the content, quality, legality and integrity of Your Data, in
          whatever form it is submitted to or generated by the Platform. By agreeing to this EULA, you represent and
          warrant that you have all necessary rights, permissions, and lawful bases to provide Your Data to the Platform
          and to allow NGTECO to process Your Data as described in this EULA.</ContentTypography>
        <br/>
        <ContentTypography>NGTECO treats Your Data as confidential and will not disclose Your Data to third parties
          except: (a) to subprocessors and service providers as reasonably necessary to provide, secure and maintain the
          Services; (b) as required by law, regulation, legal process or government request; or (c) as otherwise
          expressly permitted by you in writing.</ContentTypography>
        <br/>
        <ContentTypography>NGTECO may access and process Your Data solely for the following purposes: (i) providing,
          operating and supporting the Services; (ii) applying upgrades and resolving support issues; (iii) monitoring
          system performance, security and usage; and (iv) complying with legal obligations. NGTECO does not use Your
          Data to build or enrich profiles for its own independent purposes unrelated to providing the
          Services.</ContentTypography>
        <br/>
        <ContentTypography>You remain responsible for configuring the Platform in a manner that complies with applicable
          laws and your internal policies, including with respect to which categories of personal data you choose to
          process via the Platform.</ContentTypography>
        <br/>
        <H6>F. Security and Breach Notification</H6>
        <ContentTypography>NGTECO implements commercially reasonable administrative, technical, and physical safeguards
          designed to protect the security and integrity of the Platform and Your Data, consistent with generally
          accepted industry standards.</ContentTypography>
        <br/>
        <ContentTypography>However, you acknowledge that no system can be guaranteed as completely secure. You are
          responsible for maintaining appropriate backups of Your Data and for implementing appropriate access controls
          (such as strong passwords and user role permissions) within your organization.</ContentTypography>
        <br/>
        <ContentTypography>In the event of a confirmed personal data breach affecting Your Data, NGTECO will provide
          notification to the Customer without undue delay and in accordance with applicable data protection laws,
          including, where applicable, the General Data Protection Regulation (GDPR). Such notice may be provided by
          email to your designated contact or by other reasonable means.</ContentTypography>
        <br/>
        <H6>G. Warranties, Disclaimers, and Exclusive Remedies</H6>
        <ContentTypography>NGTECO warrants that the Services will be provided in all material respects in accordance
          with this EULA and the applicable documentation. Your exclusive remedy for a breach of this warranty shall be,
          at NGTECO’s option, (a) re-performance of the non-conforming Services, or (b) a service credit in an amount
          proportionate to the impact of the non-conformity, up to a maximum of the fees paid for the affected Services
          for the month in which the breach occurred.</ContentTypography>
        <br/>
        <ContentTypography>EXCEPT AS EXPRESSLY PROVIDED IN THIS SECTION, THE PLATFORM, DEVICES AND SERVICES ARE PROVIDED
          “AS IS” AND “AS AVAILABLE,” AND NGTECO MAKES NO OTHER WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED,
          STATUTORY OR OTHERWISE, INCLUDING WITHOUT LIMITATION ANY IMPLIED WARRANTIES OF MERCHANTABILITY, SATISFACTORY
          QUALITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, OR NON-INFRINGEMENT, OR WARRANTIES ARISING FROM A COURSE OF
          DEALING OR USAGE OF TRADE. NGTECO DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE, OR
          THAT ALL DEFECTS WILL BE CORRECTED.</ContentTypography>
        <br/>
        <H6>H. End of Agreement</H6>
        <ContentTypography>Services provided under this EULA shall be provided for the period defined in the applicable
          Order and any renewals (the “Service Term”), unless terminated earlier in accordance with this
          EULA.</ContentTypography>
        <br/>
        <ContentTypography>NGTECO may suspend or terminate your access to the Platform or Services immediately upon
          notice if: (a) you fail to pay any undisputed fees when due and do not cure such non-payment within thirty
          (30) days after NGTECO’s notice; or (b) you materially breach this EULA and fail to cure such breach within
          thirty (30) days after NGTECO’s notice (or immediately if the breach cannot be cured).</ContentTypography>
        <br/>
        <ContentTypography>Upon expiration or termination of the Service Term, all rights to access or use the Services
          shall end, subject to your limited right to retrieve Your Data as described below.</ContentTypography>
        <br/>
        <H6>I. Retrieval of Your Data</H6>
        <ContentTypography>Following expiration or termination of this EULA, NGTECO may, at its discretion, permit you
          to access the Services for a limited period not exceeding thirty (30) days solely to retrieve Your Data.
          NGTECO has no obligation to retain Your Data beyond this retrieval period and may thereafter permanently
          delete or anonymize Your Data, except to the extent NGTECO is required by applicable law to retain certain
          information for a longer period.</ContentTypography>
        <br/>
        <ContentTypography>You acknowledge that it is your responsibility to export and back up Your Data regularly
          during the Service Term.</ContentTypography>
        <br/>
        <H6>J. Fees</H6>
        <ContentTypography>You agree to pay all fees and charges specified in the applicable Order and Subscription
          Agreement. All fees are non-cancelable and, except as expressly provided otherwise in the Order or
          Subscription Agreement, non-refundable.</ContentTypography>
        <br/>
        <ContentTypography>You are responsible for all taxes, duties and similar governmental assessments (excluding
          taxes based on NGTECO’s net income) associated with the Services, which shall be invoiced in addition to the
          fees where applicable.</ContentTypography>
        <br/>
        <H6>K. Nondisclosure</H6>
        <ContentTypography>By virtue of this EULA and the Services, the parties may have access to information that is
          confidential to one another (“Confidential Information”). Confidential Information includes Your Data, the
          terms and pricing of Orders, non-public business and technical information, and any information clearly
          identified as confidential at the time of disclosure.</ContentTypography>
        <br/>
        <ContentTypography>Each party agrees: (a) to protect the other party’s Confidential Information using the same
          degree of care that it uses to protect its own confidential information of like nature, but in no event less
          than reasonable care; and (b) not to use the other party’s Confidential Information for any purpose outside
          the scope of this EULA, except with the disclosing party’s prior written permission.</ContentTypography>
        <br/>
        <H6>L. Entire Agreement</H6>
        <ContentTypography>This EULA, together with the applicable Order, Subscription Agreement, and any other
          documents expressly incorporated by reference, constitutes the entire agreement between you and NGTECO
          relating to the Services and supersedes all prior or contemporaneous agreements, proposals or representations,
          written or oral, concerning its subject matter.</ContentTypography>
        <br/>
        <ContentTypography>If any term of this EULA is found to be invalid or unenforceable, the remaining provisions
          will remain in full force and effect, and the invalid term will be replaced by a valid term that best reflects
          the parties’ original intent.</ContentTypography>
        <br/>
        <H6>M. Limitation of Liability</H6>
        <ContentTypography>TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY SHALL BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, PUNITIVE, OR CONSEQUENTIAL DAMAGES, OR ANY LOSS OF PROFITS OR REVENUE, DATA, OR DATA USE,
          ARISING OUT OF OR IN CONNECTION WITH THIS EULA OR THE USE OR PERFORMANCE OF THE SERVICES, EVEN IF ADVISED OF
          THE POSSIBILITY OF SUCH DAMAGES.</ContentTypography>
        <br/>
        <ContentTypography>NGTECO’S AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THIS EULA, WHETHER IN CONTRACT,
          TORT OR OTHERWISE, SHALL IN NO EVENT EXCEED THE TOTAL AMOUNTS ACTUALLY PAID BY YOU TO NGTECO FOR THE SERVICES
          UNDER THE ORDER GIVING RISE TO THE CLAIM DURING THE TWELVE (12) MONTH PERIOD IMMEDIATELY PRECEDING THE EVENT
          GIVING RISE TO SUCH CLAIM.</ContentTypography>
        <br/>
        <H6>N. Choice of Law and Dispute Resolution</H6>
        <ContentTypography>For Customers located in the United States, this EULA is governed by the substantive and
          procedural laws of the State of California, USA, without regard to its conflict of laws principles. Any
          dispute arising out of or relating to this EULA or the Services shall be resolved by final, binding and
          confidential arbitration administered by JAMS in California, in accordance with its applicable rules. Judgment
          on the award may be entered in any court of competent jurisdiction.</ContentTypography>
        <br/>
        <ContentTypography>For Customers located in the European Union or United Kingdom, this EULA shall be governed by
          the laws of the applicable EU Member State or, for UK Customers, the laws of England and Wales. Any disputes
          shall be submitted to the exclusive jurisdiction of the competent courts of such jurisdiction, unless
          mandatory consumer protection rules provide otherwise.</ContentTypography>
        <br/>
        <H6>O. Other</H6>
        <ContentTypography>NGTECO is an independent contractor, and nothing in this EULA shall be construed as creating
          a partnership, joint venture, or agency relationship between the parties.</ContentTypography>
        <br/>
        <ContentTypography>You shall obtain at your sole expense any rights and consents from third parties necessary
          for NGTECO to perform the Services and for Your Data to be processed in accordance with this
          EULA.</ContentTypography>
        <br/>
        <ContentTypography>You may not assign or transfer this EULA or any of your rights or obligations hereunder
          without NGTECO’s prior written consent, and any attempted assignment in breach of this section shall be null
          and void. NGTECO may assign this EULA in connection with a merger, acquisition, corporate reorganization, or
          sale of all or substantially all of its assets.</ContentTypography>
        <br/>
        <ContentTypography>Except for actions for non-payment or breach of NGTECO’s intellectual property rights, no
          action, regardless of form, arising out of or relating to this EULA may be brought by either party more than
          two (2) years after the cause of action has accrued.</ContentTypography>
        <br/>
        <H6>P. Force Majeure</H6>
        <ContentTypography>Neither party shall be responsible for any failure or delay in performance (except for
          payment obligations) if caused by: an act of war, hostility, or sabotage; act of God; electrical, internet, or
          telecommunication outage not caused by the obligated party; government restrictions; epidemic or pandemic; or
          any other event outside the reasonable control of the obligated party. The affected party shall use reasonable
          efforts to resume performance as soon as practicable.</ContentTypography>
        <br/>
        <H6>Q. Restrictions on Use of the Services</H6>
        <ContentTypography>You agree not to use, and not to permit any third party to use, the Platform or Services in
          any manner that: (a) violates applicable laws, regulations or third-party rights; (b) is fraudulent, deceptive
          or misleading; or (c) introduces malicious code or otherwise interferes with the security or operation of the
          Services.</ContentTypography>
        <br/>
        <ContentTypography>NGTECO reserves the right to remove or disable access to any content, account or
          configuration that NGTECO reasonably believes violates this section, without liability to
          you.</ContentTypography>
        <br/>
        <H6>R. Biometric Data Disclosure and Authorization</H6>
        <ContentTypography>Customers are solely responsible for compliance with all applicable biometric and privacy
          laws, including but not limited to the Illinois Biometric Information Privacy Act (BIPA) and applicable EU and
          UK data protection laws when processing biometric data of Employees.</ContentTypography>
        <br/>
        <ContentTypography>Prior to any biometric enrollment or collection via a Device or the Platform, and where
          required by law, Customers must:</ContentTypography>
        <br/>
        <ContentTypography>1. Provide Employees with clear, written notice that biometric data is being collected or
          captured, and identify the entities (including NGTECO and its cloud infrastructure providers) involved in
          processing such data.</ContentTypography>
        <br/>
        <ContentTypography>2. Inform Employees in writing of the specific purpose and length of time for which their
          biometric data is being collected, stored and used.</ContentTypography>
        <br/>
        <ContentTypography>3. Obtain the Employee’s prior written consent, or establish another lawful basis under
          applicable law, before collecting, using, or disclosing biometric data.</ContentTypography>
        <br/>
        <ContentTypography>Biometric templates are stored locally on the Device by default and are not uploaded to the
          cloud unless expressly enabled and configured by the Customer. If the Customer enables any feature that causes
          biometric data or templates to be transmitted to the cloud, the Customer remains solely responsible for
          ensuring that all necessary notices, consents, and legal bases have been obtained.</ContentTypography>
        <br/>
        <ContentTypography>NGTECO acts strictly as a data processor with respect to biometric data processed through the
          Platform and Devices and does not sell, lease, trade, or otherwise profit from biometric data, provided that
          NGTECO may receive standard service fees for products and services that utilize biometric
          functionality.</ContentTypography>
        <br/>
        <H6>S. Retention Schedule</H6>
        <ContentTypography>Biometric Data shall be deleted upon the earlier of: (a) the Employee’s termination or
          departure from the role for which biometric verification is used; (b) the Customer’s request for deletion; or
          (c) the date on which the initial purpose for collecting or obtaining such biometric data has been fulfilled,
          unless a longer retention period is expressly required by applicable law.</ContentTypography>
        <br/>
        <ContentTypography>Upon termination of Services, NGTECO may permanently delete or anonymize all remaining
          Customer Data, including any biometric identifiers stored in the cloud (if such cloud storage has been enabled
          by the Customer), after a commercially reasonable retrieval period not exceeding thirty (30) days, except
          where a longer retention period is required by law.</ContentTypography>
        <br/>
        <ContentTypography>Customers may delete biometric templates stored on Devices directly via the Device or
          Platform tools, where such tools are provided.</ContentTypography>
        <br/>
        <H6>T. Data Storage</H6>
        <ContentTypography>You shall use a reasonable standard of care to store, transmit and protect from disclosure
          any biometric data or other confidential data you control in connection with your use of the Devices and
          Platform. Such storage, transmission, and protection shall be performed in a manner that is at least as
          protective as the manner in which you store, transmit and protect other confidential and sensitive
          information, such as account numbers, personal identifiers or financial information, and in all cases in
          compliance with applicable law.</ContentTypography>
        <br/>
        <ContentTypography>NGTECO hosts its cloud services on commercially reputable cloud infrastructure providers,
          including Amazon Web Services (AWS). The specific data center locations may change from time to time based on
          operational and compliance needs.</ContentTypography>
        <br/>
        <H6>U. Mobile Application License</H6>
        <ContentTypography>If you access the Platform via a mobile application, NGTECO grants you a revocable,
          non-exclusive, non-transferable, limited right to install and use the mobile application on compatible
          wireless electronic devices that you own or control, and to access and use the mobile application on such
          devices strictly in accordance with this EULA.</ContentTypography>
        <br/>
        <ContentTypography>You shall not: (a) decompile, reverse engineer, disassemble, attempt to derive the source
          code of, or decrypt the mobile application; (b) make any modification, adaptation, improvement, translation,
          or derivative work from the application; (c) use the application for any revenue-generating endeavor or
          commercial enterprise not expressly permitted by this EULA; (d) make the application available over a network
          permitting access by multiple devices or users at the same time; or (e) remove, alter, or obscure any
          proprietary notice.</ContentTypography>
        <br/>
        <H6>V. Renewals</H6>
        <ContentTypography>In order to provide continuous service, certain paid Subscriptions may automatically renew
          in accordance with the separate NGTECO Subscription Agreement, which you must
          expressly accept before any automatic renewal or recurring charges occur. Details
          regarding renewal periods, cancellation rights, and any applicable advance notices will be
          provided in the Subscription Agreement and at the time of purchase.</ContentTypography>
        <br/>
        <ContentTypography>If you do not wish your Subscription to renew automatically, you must follow the
          cancellation procedures described in the Subscription Agreement and within the Platform or applicable app
          store settings, where relevant.</ContentTypography>
        <br/>
        <H6>W. Customer Reference</H6>
        <ContentTypography>You agree that NGTECO may identify you as a customer and use your company name and logo in
          sales presentations, websites and marketing materials, provided that NGTECO will cease such use upon your
          reasonable written request.</ContentTypography>
        <br/>
        <H6>X. Contact Information</H6>
        <ContentTypography>If you have a dispute with NGTECO or wish to provide notice under this EULA, please
          contact:</ContentTypography>
        <br/>
        <ContentTypography>NGTECO</ContentTypography>
        <ContentTypography>Unit B, 20/F, COS Centre, 52–56 Tsun Yip Street, Kwun Tong, Kowloon, Hong Kong
          SAR</ContentTypography>
        <br/>
        <ContentTypography>Support Email: ngtime@ngteco.com</ContentTypography>
        <ContentTypography>Support Phone (US): +1 770 800 2321</ContentTypography>
        <ContentTypography>Support Phone (CA): +1 438 300 8567</ContentTypography>
        <ContentTypography>Support Phone (DE): +49 322 1224 6737</ContentTypography>
        <ContentTypography>Support Phone (UK): +44 808 812 6646</ContentTypography>
        <ContentTypography>Support Phone (ES): +34 919 942 161</ContentTypography>
        <ContentTypography>Support Phone (JP): +81 800-300-8197</ContentTypography>
        <ContentTypography>Support Phone (MX): +52 800 649 0344</ContentTypography>
        <ContentTypography>Support Phone (MY): +60 3 9219 1725</ContentTypography>
        <br/>
        <ContentTypography>BY CLICKING “I ACCEPT”, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD AND AGREE TO BE BOUND
          BY THE TERMS AND CONDITIONS OF THIS EULA.</ContentTypography>
      </Box>
    </Box>
  )
}

export default NGUserAgreement
